import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { CircleDollarSign, Save, Loader2, Sparkles, Info } from "lucide-react";
import { DEFAULT_PRICING, formatCHF, clearPricingCache } from "@/lib/pricing";

const AI_TIERS = [30, 90, 365];
const PLAN_LABELS = { einzel: "Einzel-Lizenz", team: "Team-Lizenz" };

function normalize(p) {
  const d = DEFAULT_PRICING;
  return {
    currency: p?.currency || "CHF",
    license: {
      einzel: Number(p?.license?.einzel ?? d.license.einzel),
      team: Number(p?.license?.team ?? d.license.team),
    },
    ai_extension: {
      einzel: Object.fromEntries(AI_TIERS.map((t) => [t, Number(p?.ai_extension?.einzel?.[t] ?? d.ai_extension.einzel[t])])),
      team: Object.fromEntries(AI_TIERS.map((t) => [t, Number(p?.ai_extension?.team?.[t] ?? d.ai_extension.team[t])])),
    },
  };
}

export default function AdminPricing() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing")
      .then((r) => setPricing(normalize(r.data)))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const setLic = (plan, val) =>
    setPricing((p) => ({ ...p, license: { ...p.license, [plan]: val } }));
  const setAi = (plan, days, val) =>
    setPricing((p) => ({ ...p, ai_extension: { ...p.ai_extension, [plan]: { ...p.ai_extension[plan], [days]: val } } }));

  const save = async () => {
    const vals = [
      pricing.license.einzel,
      pricing.license.team,
      ...AI_TIERS.map((t) => pricing.ai_extension.einzel[t]),
      ...AI_TIERS.map((t) => pricing.ai_extension.team[t]),
    ];
    if (vals.some((v) => !(Number.isFinite(Number(v)) && Number(v) >= 1))) {
      toast.error("Bitte geben Sie für jedes Feld einen gültigen Preis ab CHF 1 ein.");
      return;
    }
    setSaving(true);
    try {
      const body = { license: pricing.license, ai_extension: pricing.ai_extension };
      const r = await api.put("/admin/pricing", body);
      setPricing(normalize(r.data));
      clearPricingCache();
      toast.success("Preise gespeichert – Änderungen sind sofort aktiv (kein Deploy nötig).");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const PriceInput = ({ value, onChange, testid }) => (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">CHF</span>
      <input
        type="number"
        min={1}
        step={1}
        value={Number.isFinite(value) ? value : ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        data-testid={testid}
        className="w-40 bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-2.5 outline-none focus:border-gold/50 focus:ring-2 focus:ring-gold/20 transition-all text-foreground"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-10" data-testid="admin-pricing">
        <div className="flex items-center gap-3 mb-2">
          <CircleDollarSign className="h-6 w-6 text-gold" />
          <h1 className="font-heading font-extrabold text-3xl">Verkaufspreise</h1>
        </div>
        <p className="text-muted-foreground mb-8 max-w-2xl">
          Passen Sie hier alle Verkaufspreise an. Änderungen greifen <strong className="text-foreground/90">sofort</strong> –
          im Checkout wie in der Anzeige – ganz ohne erneutes Deployment. Alle Beträge sind Nettopreise (exkl. MWST) in CHF.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Preise werden geladen…
          </div>
        ) : (
          <div className="space-y-6">
            {/* Kurslizenzen */}
            <div className="rounded-2xl glass-strong p-6" data-testid="pricing-licenses">
              <h2 className="font-heading font-bold text-lg mb-1">Kurslizenzen</h2>
              <p className="text-sm text-muted-foreground mb-5">Einmaliger Kaufpreis pro Lizenz.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                {["einzel", "team"].map((plan) => (
                  <div key={plan} className="rounded-xl bg-white/5 border border-white/10 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-heading font-semibold">{PLAN_LABELS[plan]}</span>
                      <span className="text-xs text-gold">{formatCHF(Number(pricing.license[plan]) || 0)}</span>
                    </div>
                    <PriceInput
                      value={pricing.license[plan]}
                      onChange={(v) => setLic(plan, v)}
                      testid={`pricing-license-${plan}-input`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* KI-Verlängerungen */}
            <div className="rounded-2xl glass-strong p-6" data-testid="pricing-ai">
              <div className="flex items-center gap-2.5 mb-1">
                <Sparkles className="h-5 w-5 text-gold" />
                <h2 className="font-heading font-bold text-lg">KI-Coach Verlängerungen</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Preis je Verlängerungs-Paket und Lizenztyp.</p>
              <div className="grid sm:grid-cols-2 gap-6">
                {["einzel", "team"].map((plan) => (
                  <div key={plan} className="rounded-xl bg-white/5 border border-white/10 p-4">
                    <span className="font-heading font-semibold block mb-3">{PLAN_LABELS[plan]}</span>
                    <div className="space-y-3">
                      {AI_TIERS.map((days) => (
                        <div key={days} className="flex items-center justify-between gap-3">
                          <span className="text-sm text-foreground/80 w-20">{days} Tage</span>
                          <PriceInput
                            value={pricing.ai_extension[plan][days]}
                            onChange={(v) => setAi(plan, days, v)}
                            testid={`pricing-ai-${plan}-${days}-input`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 rounded-2xl glass p-4">
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Info className="h-4 w-4 text-gold flex-shrink-0" />
                Kunden sehen die neuen Preise beim nächsten Laden der Seite.
              </p>
              <button
                onClick={save}
                disabled={saving}
                data-testid="pricing-save-button"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? "Speichern…" : "Preise speichern"}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
